/**
 * The draft editor (plan docs/plans/lms-plan.md 8.1, 6.1, 6.2, 18.11, 18.17;
 * WP-44; R8, R10, R11, R13), at /lms/manage/catalog/versions/:versionId.
 *
 * Loads GET /admin/versions/:id ({ version, course, content, questions,
 * quiz_settings, sources, lint, impact }). A draft is edited in three tabs
 * (content; questions; settings and sources) and autosaved (useDraftAutosave:
 * PUT with the changed fields, 8.1); every save brings the lint back, shown
 * as the lint's own { rule, path, message } list, and any lint problem blocks
 * Publish (the change summary is asked for in the publish dialog itself).
 * Preview opens the player's components against the draft (the learner's
 * content route never serves a draft, reads.js; plan 18.22). A published,
 * superseded or retired version is shown read-only (G7).
 *
 * The course builder (docs/plans/lms-course-builder-plan.md 8.3, 8.4, 8.5,
 * 4.10, 6.11, 18.2 Q16, 18.5; WP-B31), all of it only while GET /health says
 * the builder is on for the network (G20; otherwise the editor is exactly as
 * before):
 * - a notice at the top of a draft the builder wrote ("Built by the course
 *   builder from 3 documents on 24 Sep 2026 ($4.80) ...") with a link to the
 *   run page, and for an extended course the sections it revised and a link
 *   to the comparison;
 * - for a builder-written clinical_skills draft, the clinician sign-off state
 *   and a "Record clinician sign-off" form (the caller's password through the
 *   Team tab's PasswordField); Publish stays disabled with the reason until a
 *   current sign-off exists (enforced here and proven by the audit row; the
 *   publish route is the live module's and is not edited, 18.2 Q16);
 * - the Sources panel (builder/SourcesPanel.tsx) beside the content and
 *   questions tabs;
 * - the section actions (builder/SectionActions.tsx) in ContentEditor's slot;
 *   while a section run is active the draft is read-only, the autosave is
 *   paused, the banner names the section, and the run is polled every 10
 *   seconds (4.10); on done the version is reloaded, on a failure the cause is
 *   shown and editing comes back.
 *
 * The course editor, "Edit with AI" (docs/plans/lms-course-editor-plan.md 8.1,
 * 8.5 to 8.7, 6.14, 18.2 Q3, Q4 and Q15, 18.3, 18.5; WP-E31; R1, R3, R8, R9),
 * all of it only while GET /health says builder.enabled, network_allowed AND
 * builder.editor.enabled (G25; otherwise the editor is exactly as before), and
 * never on an SOP or read-and-acknowledge course (the edit routes refuse them):
 * - an "Edit with AI" button in the header of a draft toggles the panel
 *   (editor/EditPanel.tsx, WP-E30, with the props of 8.2). The query ?edit=1
 *   is the panel's open state, so a page reached with it opens the panel and
 *   an in-place reload keeps it open. The panel is a right-hand column on
 *   wide screens and a full-width section above the editor on narrow ones;
 * - onBeforeSend is the autosave flush (a failed save stops the send). After
 *   it the version's run is read again within a second or two and then every
 *   10 seconds, so the message's run locks the draft by the existing rule
 *   (8.5); the banner then names the message ("Edit with AI is working on
 *   message n ..."), found by the run's id in GET .../edits (18.5; the run is
 *   told apart with isEditRun, 18.3);
 * - when an edit run ends, or the panel reports an applied message
 *   (onApplied), the version is read again: a changed content hash reloads
 *   the editor in place with a toast; clarify, declined, no change, failed and
 *   cancelled unlock without a reload (8.5);
 * - Publish is held while any applied message waits for Accept or Revert
 *   (18.2 Q4; enforced here like the clinician sign-off, fail closed while the
 *   list cannot be read), and a clinical_skills draft an edit run changed
 *   needs a current clinician sign-off (18.2 Q3; the server counts edit runs
 *   as writers, so the sign-off route answers required);
 * - "Use the edit notes" appends the applied messages' change notes to the
 *   draft's change summary in the editor's own state (the autosave saves it,
 *   and the admin can reword it before publishing, 8.6);
 * - the builder notice's origin is the run of mode new or extend: an edit run
 *   never reads as the draft's origin, and only section runs count as passes
 *   (6.14);
 * - a published version with messages shows "Edited with AI: n messages",
 *   which opens the panel read-only as the version's history (8.6);
 * - the Sources panel names a stated fact's message through the same list
 *   ("Stated by <name> in message n", 8.7), and its link opens the panel.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Eye, GitCompare, History, ListPlus, Loader2, Send, ShieldCheck, Sparkles, Trash2 } from 'lucide-react';
import ConfirmModal from '../../../../ui/ConfirmModal';
import Modal from '../../../../ui/Modal';
import SegmentedControl from '../../../../ui/SegmentedControl';
import { useToast } from '../../../../ui/Toast';
import { outlineBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { apiErrorMessage } from '../../../api/client';
import {
  CLINICIAN_NAME_RANGE,
  SIGNOFF_STATEMENT_RANGE,
  formatCents,
  getBuilderConfig,
  getBuilderRun,
  getClinicalSignoff,
  getVersionRun,
  isTerminalRunStatus,
  listBuilderRuns,
  readBuilderError,
  recordClinicalSignoff,
} from '../../../api/builder';
import type { BuilderRun, BuilderSource, ClinicalSignoffState } from '../../../api/builder';
import { discardDraft, getVersion, lintVersion } from '../../../api/catalog';
import type { DraftPatch, EditorQuestion, LintResult, PublishResult, VersionDetail } from '../../../api/catalog';
import { EDITOR_MESSAGES_MAX_LIMIT, isEditRun, listEditMessages, readEditorError } from '../../../api/editor';
import type { EditMessage } from '../../../api/editor';
import type { CourseContent, CourseSource, QuizSettings, VersionQuizSettings } from '../../../content/types';
import { formatDay } from '../../../components/player/DoneStage';
import { noticeCls, pillCls } from '../../../components/player/styles';
import { PasswordField, signingErrorText } from '../../team/signoffs/PasswordField';
import { builderPaths, explainError, isBuildingStatus } from './builder/runModel';
import { SectionActions } from './builder/SectionActions';
import { SourcesPanel } from './builder/SourcesPanel';
import { useBuildRun, useBuilderAvailability } from './builder/useBuildRun';
import { EditPanel } from './editor/EditPanel';
import { TextArea, TextInput } from './fields';
import { ContentEditor } from './ContentEditor';
import { blockingErrors, LintPanel } from './LintPanel';
import {
  catalogPaths,
  KIND_LABELS,
  lintQuestionIndex,
  profileFor,
  profileHint,
  questionInputs,
  STATUS_LABELS,
  STATUS_TONES,
} from './model';
import { PublishDialog } from './PublishDialog';
import { VersionPreview } from './VersionPreview';
import { QuestionsEditor } from './QuestionsEditor';
import { SettingsEditor } from './SettingsEditor';
import { cardCls, hintCls, linkCls } from './styles';
import { useDraftAutosave } from './useDraftAutosave';
import type { DraftField } from './useDraftAutosave';

type EditorTab = 'content' | 'questions' | 'settings';

interface DraftState {
  content: CourseContent;
  questions: EditorQuestion[];
  quiz_settings: VersionQuizSettings;
  sources: CourseSource[];
  change_summary: string | null;
  estimated_minutes: number | null;
}

function draftOf(detail: VersionDetail): DraftState {
  return {
    content: detail.content,
    questions: Array.isArray(detail.questions) ? detail.questions : [],
    quiz_settings: detail.quiz_settings || {},
    sources: Array.isArray(detail.sources) ? detail.sources : [],
    change_summary: detail.version.change_summary ?? null,
    estimated_minutes: detail.version.estimated_minutes ?? null,
  };
}

/** 6.3: three sources for compliance, one for an SOP course, two otherwise. */
function minimumSources(category: string): number {
  if (category === 'compliance') return 3;
  if (category === 'sop') return 1;
  return 2;
}

function SaveStatus({ state, error, savedAt, onRetry }: { state: string; error: string | null; savedAt: Date | null; onRetry: () => void }) {
  let text: string;
  switch (state) {
    case 'saving':
      text = 'Saving...';
      break;
    case 'dirty':
      text = 'Unsaved changes';
      break;
    case 'saved':
      text = savedAt ? `Saved at ${savedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Saved';
      break;
    case 'error':
      text = error || 'The draft could not be saved.';
      break;
    default:
      text = 'All changes saved';
  }
  return (
    <span className="inline-flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400" aria-live="polite" data-testid="lms-save-status" data-state={state}>
      {state === 'saving' ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
      {state === 'error' ? <AlertTriangle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" aria-hidden="true" /> : null}
      <span className={state === 'error' ? 'text-red-700 dark:text-red-400' : undefined}>{text}</span>
      {state === 'error' ? (
        <button type="button" className={`${linkCls} text-xs`} onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// The course builder's part of the editor (builder plan 8.3 to 8.5, 18.2 Q16)
// ---------------------------------------------------------------------------

/** 4.10: the editor polls a section run every 10 seconds. */
export const EDITOR_RUN_POLL_MS = 10000;

/** Appendix B default of LMS_BUILDER_MAX_SECTION_RUNS_PER_VERSION, when the config route cannot be read. */
const DEFAULT_SECTION_PASSES = 10;

interface VersionBuilderState {
  loaded: boolean;
  /** The run (mode new or extend) that wrote this draft, or null for a hand-made draft. */
  origin: BuilderRun | null;
  /** The origin run's sources (documents and web links), or null when not read. */
  originSources: BuilderSource[] | null;
  /** A run of this version that is building now (a section run, 6.11, or an edit run, editor plan 8.5), or null. */
  active: BuilderRun | null;
  /** Section runs made on this draft, or null when the run list could not be read. */
  passesUsed: number | null;
  passesMax: number;
  /** True when a finished builder run (mode new, extend or section) of this draft exists (its extraction can be reused). */
  hasDoneRun: boolean;
  /** True when an Edit with AI run (mode edit) of this draft exists (editor plan 6.14, 18.2 Q3). */
  hasEditRun: boolean;
  /** True when an Edit with AI run of this draft finished (its provenance rows may be on the draft, 8.7). */
  hasDoneEditRun: boolean;
  signoff: ClinicalSignoffState | null;
  signoffError: string | null;
}

const EMPTY_BUILDER_STATE: VersionBuilderState = {
  loaded: false,
  origin: null,
  originSources: null,
  active: null,
  passesUsed: null,
  passesMax: DEFAULT_SECTION_PASSES,
  hasDoneRun: false,
  hasEditRun: false,
  hasDoneEditRun: false,
  signoff: null,
  signoffError: null,
};

/**
 * What the editor needs to know about the builder for this version, read
 * once per mount (and again on refresh): the version's run (active or last),
 * the course's runs (the origin run, the section passes used and the edit
 * runs), the section-pass limit, the origin run's sources, and for a clinical
 * course the clinician sign-off state (18.2 Q16; editor plan 18.2 Q3).
 */
function useVersionBuilder(available: boolean, versionId: string, courseId: string, clinical: boolean) {
  const [state, setState] = useState<VersionBuilderState>(EMPTY_BUILDER_STATE);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!available) return undefined;
    let cancelled = false;
    (async () => {
      const [versionRun, runs, config] = await Promise.all([
        getVersionRun(versionId).catch(() => null),
        listBuilderRuns({ courseId })
          .then((list) => (Array.isArray(list?.items) ? list.items : []))
          .catch(() => null),
        getBuilderConfig().catch(() => null),
      ]);
      const mine = (runs || []).filter((r) => r.version_id === versionId);
      // Editor plan 6.14: the run that wrote the draft is of mode new or extend; an edit run never reads as its origin.
      const origin = mine.find((r) => r.mode === 'new' || r.mode === 'extend') ?? null;
      const passesUsed = runs ? mine.filter((r) => r.mode === 'section').length : null;
      const hasDoneRun = mine.some((r) => r.status === 'done' && !isEditRun(r));
      const hasEditRun = mine.some((r) => isEditRun(r));
      const hasDoneEditRun = mine.some((r) => isEditRun(r) && r.status === 'done');
      const active = versionRun && versionRun.version_id === versionId && isBuildingStatus(versionRun.status) ? versionRun : null;
      const max = config?.limits?.max_section_runs_per_version;
      let originSources: BuilderSource[] | null = null;
      if (origin) {
        originSources = await getBuilderRun(origin.id)
          .then((d) => (Array.isArray(d?.sources) ? d.sources : []))
          .catch(() => null);
      }
      let signoff: ClinicalSignoffState | null = null;
      let signoffError: string | null = null;
      // Editor plan 18.2 Q3: an AI edit of a clinical draft, built or hand-made, needs the sign-off too (the server says required).
      if (clinical && (origin || hasEditRun)) {
        try {
          signoff = await getClinicalSignoff(versionId);
        } catch (err) {
          signoffError = readBuilderError(err, 'The clinician sign-off could not be checked.').message;
        }
      }
      if (cancelled) return;
      setState({
        loaded: true,
        origin,
        originSources,
        active,
        passesUsed,
        passesMax: typeof max === 'number' && max > 0 ? max : DEFAULT_SECTION_PASSES,
        hasDoneRun,
        hasEditRun,
        hasDoneEditRun,
        signoff,
        signoffError,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [available, versionId, courseId, clinical, tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);
  /** A section run was just created: lock at once and count the pass. */
  const started = useCallback((run: BuilderRun) => {
    setState((s) => ({ ...s, active: run, passesUsed: s.passesUsed === null ? null : s.passesUsed + 1 }));
  }, []);
  /** A run of this version was found building (an edit message's run, editor plan 8.5): lock without counting an edit as a pass. */
  const adopt = useCallback((run: BuilderRun) => {
    setState((s) => {
      if (s.active) return s;
      const section = run.mode === 'section';
      return {
        ...s,
        active: run,
        hasEditRun: s.hasEditRun || isEditRun(run),
        passesUsed: section && s.passesUsed !== null ? s.passesUsed + 1 : s.passesUsed,
      };
    });
  }, []);
  /** The active run ended without a new draft: unlock. */
  const ended = useCallback(() => setState((s) => ({ ...s, active: null })), []);

  return { ...state, refresh, started, adopt, ended };
}

/** "2, 4 and the closing section". */
function joinWords(words: string[]): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/** 8.5: what an extend run revised, in words, from its targets (the added sections show in the comparison). */
function revisedLine(run: BuilderRun, content: CourseContent): string {
  const sections = Array.isArray(content.sections) ? content.sections : [];
  const ids = Array.isArray(run.targets?.replace_section_ids) ? run.targets.replace_section_ids : [];
  const numbers = ids
    .map((id) => sections.findIndex((s) => s.id === id))
    .filter((i) => i >= 0)
    .sort((a, b) => a - b)
    .map((i) => String(i + 1));
  const parts: string[] = [];
  if (numbers.length > 0) parts.push(`${numbers.length === 1 ? 'section' : 'sections'} ${joinWords(numbers)}`);
  if (run.targets?.keep_closing === false) parts.push('the closing section');
  return parts.length > 0 ? `revised ${joinWords(parts)} and` : '';
}

function BuilderNotice({
  origin,
  sources,
  content,
  diffTo,
}: {
  origin: BuilderRun;
  sources: BuilderSource[] | null;
  content: CourseContent;
  /** The comparison link for an extended course, or null. */
  diffTo: string | null;
}) {
  const count = sources ? sources.length : null;
  const docs = count === null ? 'its documents' : `${count} document${count === 1 ? '' : 's'}`;
  const day = formatDay(origin.finished_at || origin.created_at);
  const revised = origin.mode === 'extend' ? revisedLine(origin, content) : '';
  return (
    <div className={noticeCls('primary')} role="status" data-testid="lms-builder-notice">
      <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <div className="min-w-0 space-y-1">
        <p>
          {`Built by the course builder from ${docs}${day ? ` on ${day}` : ''} (${formatCents(origin.spent_cents)}). Read every slide and question before publishing. `}
          <Link to={builderPaths.run(origin.id)} className={linkCls} data-testid="lms-builder-notice-run">
            See the build
          </Link>
        </p>
        {origin.mode === 'extend' ? (
          <p data-testid="lms-builder-notice-extend">
            {`The builder ${revised ? `${revised} ` : ''}added the new documents' material to this course; compare with the current version before publishing. `}
            {diffTo ? (
              <Link to={diffTo} className={linkCls}>
                Compare with current
              </Link>
            ) : null}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function ClinicalSignoffBlock({
  versionId,
  state,
  error,
  aiOnly,
  onRecorded,
  onRetry,
}: {
  versionId: string;
  state: ClinicalSignoffState | null;
  error: string | null;
  /** True for a draft the builder did not write but Edit with AI changed (editor plan 18.2 Q3): the words name the editor. */
  aiOnly: boolean;
  onRecorded: () => void;
  onRetry: () => void;
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [statement, setStatement] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (error) {
    return (
      <div className={noticeCls('red')} role="alert" data-testid="lms-clinical-signoff" data-state="error">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <div className="space-y-1">
          <p>{`${error} Publishing stays blocked until the sign-off can be checked.`}</p>
          <button type="button" className={`${linkCls} text-sm`} onClick={onRetry}>
            Check again
          </button>
        </div>
      </div>
    );
  }
  if (!state || !state.required) return null;

  const close = () => {
    if (saving) return;
    setOpen(false);
    setPassword('');
    setFormError(null);
  };

  const submit = async () => {
    if (saving) return;
    const n = name.trim();
    const s = statement.trim();
    if (n.length < CLINICIAN_NAME_RANGE[0] || n.length > CLINICIAN_NAME_RANGE[1]) {
      setFormError(`Enter the clinician's name (${CLINICIAN_NAME_RANGE[0]} to ${CLINICIAN_NAME_RANGE[1]} characters).`);
      return;
    }
    if (s.length < SIGNOFF_STATEMENT_RANGE[0] || s.length > SIGNOFF_STATEMENT_RANGE[1]) {
      setFormError(`Write the statement (${SIGNOFF_STATEMENT_RANGE[0]} to ${SIGNOFF_STATEMENT_RANGE[1]} characters).`);
      return;
    }
    if (!password) {
      setFormError('Enter your password to sign.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await recordClinicalSignoff(versionId, { clinician_name: n, statement: s, password });
      setPassword('');
      setOpen(false);
      toast(`Clinician sign-off by ${n} recorded.`);
      onRecorded();
    } catch (err) {
      setPassword('');
      setFormError(signingErrorText(err, 'The sign-off could not be recorded.'));
    } finally {
      setSaving(false);
    }
  };

  const signed = state.signoff;
  const current = state.current && signed;
  return (
    <div
      className={noticeCls(current ? 'green' : 'amber')}
      role="status"
      data-testid="lms-clinical-signoff"
      data-state={current ? 'current' : signed ? 'stale' : 'missing'}
    >
      <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-2">
        {current && signed ? (
          <p>
            {`Clinician sign-off: ${signed.clinician_name ?? ''}, recorded by ${signed.signed_by?.full_name ?? 'an admin'}${signed.signed_at ? ` on ${formatDay(signed.signed_at)}` : ''}. "${signed.statement ?? ''}"`}
          </p>
        ) : signed ? (
          <p>
            {aiOnly
              ? `The clinician sign-off by ${signed.clinician_name ?? 'a clinician'} was recorded before the last AI change to this draft. A clinical skills course changed with Edit with AI needs a current sign-off before it is published.`
              : `The clinician sign-off by ${signed.clinician_name ?? 'a clinician'} was recorded before the builder last changed this draft. A clinical skills course the builder wrote needs a current sign-off before it is published.`}
          </p>
        ) : aiOnly ? (
          <p>A clinical skills course changed with Edit with AI needs a named clinician&apos;s sign-off before it is published. Record it once the clinician has read the draft.</p>
        ) : (
          <p>A clinical skills course the builder wrote needs a named clinician&apos;s sign-off before it is published. Record it once the clinician has read the draft.</p>
        )}
        {!current ? (
          <button type="button" className={secondaryBtnCls} onClick={() => setOpen(true)} data-testid="lms-clinical-signoff-open">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            Record clinician sign-off
          </button>
        ) : null}
      </div>
      <Modal isOpen={open} onClose={close} title="Record clinician sign-off" maxWidth="max-w-lg">
        <div className="space-y-3" data-testid="lms-clinical-signoff-form">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            The clinician who read this draft is named here with their statement. You sign the record with your own password; the sign-off is kept in
            the audit trail.
          </p>
          <TextInput label="Clinician name" value={name} onChange={setName} testId="lms-clinical-signoff-name" />
          <TextArea
            label="Statement"
            value={statement}
            onChange={setStatement}
            rows={3}
            placeholder="I have read this course and its questions and they are clinically accurate for our site."
            hint={`${SIGNOFF_STATEMENT_RANGE[0]} to ${SIGNOFF_STATEMENT_RANGE[1]} characters.`}
            testId="lms-clinical-signoff-statement"
          />
          <PasswordField id={`lms-clinical-signoff-password-${versionId}`} value={password} onChange={setPassword} disabled={saving} testId="lms-clinical-signoff-password" />
          {formError ? (
            <div className={noticeCls('red')} role="alert" data-testid="lms-clinical-signoff-error">
              {formError}
            </div>
          ) : null}
          <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-3 dark:border-gray-700">
            <button type="button" className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`} onClick={close} disabled={saving}>
              Cancel
            </button>
            <button type="button" className={primaryBtnCls} onClick={() => void submit()} disabled={saving} data-testid="lms-clinical-signoff-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Sign
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The course editor's part ("Edit with AI"; editor plan 8.1, 8.5 to 8.7)
// ---------------------------------------------------------------------------

/**
 * 8.5: after the panel's Send the version's run is read again this soon (then
 * every EDITOR_RUN_POLL_MS), so the message's run locks the draft within a
 * second or two rather than up to ten.
 */
export const SEND_WATCH_DELAYS_MS: readonly number[] = [1000, 2000, 4000];

/** A watch that starts this soon after a Send uses the short delays above. */
const SEND_WATCH_WINDOW_MS = 5000;

/** A draft holds at most 30 messages (LMS_EDITOR_MAX_MESSAGES_PER_DRAFT); pages of 200 are read, at most this many. */
const EDIT_LIST_MAX_PAGES = 5;

/** A message id is a uuid; anything else is never put into a selector. */
const MESSAGE_ID_PATTERN = /^[0-9a-fA-F-]{36}$/;

function pageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

/** document.visibilityState as state: a hidden tab asks nothing (LMS plan D14). */
function usePageVisible(): boolean {
  const [visible, setVisible] = useState<boolean>(pageVisible);
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onChange = () => setVisible(pageVisible());
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

/** 5.2, 18.2 Q4: a message the admin still has to accept or revert (review pending exists only with outcome applied). */
export function awaitsReview(message: Pick<EditMessage, 'outcome' | 'review'>): boolean {
  return message.outcome === 'applied' && (message.review === 'pending' || message.review === null);
}

/** 8.6: the change notes of the applied messages still standing (not reverted), oldest first, blanks and repeats left out. */
export function editNotesOf(messages: readonly EditMessage[]): string[] {
  return messages
    .filter((m) => m.outcome === 'applied' && m.review !== 'reverted')
    .slice()
    .sort((a, b) => a.ordinal - b.ordinal)
    .map((m) => (typeof m.plan_summary?.change_note === 'string' ? m.plan_summary.change_note.trim() : ''))
    .filter((note, i, all) => note !== '' && all.indexOf(note) === i);
}

/** 8.6 "Use the edit notes": the summary with every note it does not hold yet appended; null when nothing is new. */
export function withEditNotes(summary: string | null | undefined, notes: readonly string[]): string | null {
  const current = typeof summary === 'string' ? summary.trim() : '';
  const missing = notes.filter((note) => !current.includes(note));
  if (missing.length === 0) return null;
  return [current, ...missing].filter((part) => part !== '').join(' ');
}

/** 8.5: the lock banner's words for an edit run (18.5: the message is found by the run's id). */
export function editLockLine(message: Pick<EditMessage, 'ordinal'> | null): string {
  return message
    ? `Edit with AI is working on message ${message.ordinal}; the draft is read-only until it finishes.`
    : 'Edit with AI is working on a message; the draft is read-only until it finishes.';
}

interface VersionEditsState {
  /** false until the first read has answered (or failed). */
  loaded: boolean;
  /** The version's messages, ordinal ascending. */
  messages: EditMessage[];
  /** The first read's failure (a later failed poll keeps the last answer and says nothing). */
  error: string | null;
}

const EMPTY_EDITS: VersionEditsState = { loaded: false, messages: [], error: null };

/**
 * GET /admin/builder/versions/:id/edits read to its last page (Appendix A),
 * for the publish hold, the lock banner's message number, the edit notes, the
 * Sources panel's names and the history link. On a draft it is read again
 * every 10 seconds while a message runs, a review is pending or the first read
 * failed (a pending review lifts once the panel accepts or reverts it).
 */
function useVersionEdits(enabled: boolean, versionId: string, options: { live: boolean; busy: boolean; visible: boolean }) {
  const { live, busy, visible } = options;
  const [state, setState] = useState<VersionEditsState>(EMPTY_EDITS);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const all: EditMessage[] = [];
        let offset = 0;
        for (let page = 0; page < EDIT_LIST_MAX_PAGES; page += 1) {
          // eslint-disable-next-line no-await-in-loop
          const list = await listEditMessages(versionId, { limit: EDITOR_MESSAGES_MAX_LIMIT, offset });
          all.push(...list.items);
          if (!list.page?.hasMore || list.items.length === 0) break;
          offset += list.items.length;
        }
        all.sort((a, b) => a.ordinal - b.ordinal);
        if (!cancelled) setState({ loaded: true, messages: all, error: null });
      } catch (err) {
        if (cancelled) return;
        const message = readEditorError(err, 'The AI edits could not be read.').message;
        setState((s) => (s.loaded && s.error === null ? s : { loaded: true, messages: s.messages, error: message }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, versionId, tick]);

  const pending = state.messages.filter(awaitsReview).length;
  const poll = enabled && live && visible && (busy || pending > 0 || state.error !== null);
  useEffect(() => {
    if (!poll) return undefined;
    const timer = setTimeout(() => setTick((t) => t + 1), EDITOR_RUN_POLL_MS);
    return () => clearTimeout(timer);
  }, [poll, tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, pending, refresh };
}

function LoadedEditor({
  initial,
  onReload,
  onReplace,
}: {
  initial: VersionDetail;
  onReload: () => void;
  /** Editor plan 8.5: the version as read again after an AI write, shown in place (a new generation remounts the editor). */
  onReplace: (detail: VersionDetail) => void;
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [meta, setMeta] = useState<VersionDetail>(initial);
  const metaRef = useRef<VersionDetail>(meta);
  metaRef.current = meta;
  const [draft, setDraft] = useState<DraftState>(() => draftOf(initial));
  const draftRef = useRef<DraftState>(draft);
  const [tab, setTab] = useState<EditorTab>('content');
  const [publishOpen, setPublishOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [busy, setBusy] = useState<'lint' | 'discard' | 'preview' | null>(null);

  const { version, course } = meta;
  const isDraft = version.status === 'draft';
  const hasQuiz = course.kind !== 'read_ack';
  const profile = profileFor(course.category);

  // The course builder (builder plan 8.3, 8.4; G20: nothing of it unless health says it is on).
  const builder = useBuilderAvailability();
  const clinical = course.category === 'clinical_skills';
  const vb = useVersionBuilder(builder.available && isDraft, version.id, course.id, clinical);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(() => {
    const first = Array.isArray(initial.content?.sections) ? initial.content.sections[0] : undefined;
    return first ? first.id : null;
  });
  const [runOutcome, setRunOutcome] = useState<string | null>(null);
  const locked = isDraft && vb.active !== null;
  const editable = isDraft && !locked;
  const editableRef = useRef(editable);
  editableRef.current = editable;
  const poll = useBuildRun(vb.active ? vb.active.id : null, { intervalMs: EDITOR_RUN_POLL_MS, enabled: vb.active !== null });
  const handledRun = useRef<string | null>(null);
  const liveRun = poll.detail && vb.active && poll.detail.run?.id === vb.active.id ? poll.detail.run : vb.active;

  // The course editor (editor plan 8.1, G25 as read by 18.2 Q15): the builder on for the network AND health's editor block on,
  // and never for an SOP or read-and-acknowledge course (the edit routes refuse them, 6.2).
  const editorOn = builder.available && builder.health?.editor?.enabled === true;
  const aiAllowed = course.category !== 'sop' && course.kind !== 'read_ack';
  const visible = usePageVisible();
  const editLocked = locked && isEditRun(vb.active);
  const edits = useVersionEdits(editorOn && aiAllowed, version.id, { live: isDraft, busy: editLocked, visible });
  const editsRef = useRef<EditMessage[]>(edits.messages);
  editsRef.current = edits.messages;
  const { refresh: refreshEdits } = edits;
  const activeMessage = editLocked && vb.active ? edits.messages.find((m) => m.run_id === vb.active?.id) ?? null : null;

  const read = useCallback((): Required<DraftPatch> => {
    const d = draftRef.current;
    return {
      content: d.content,
      questions: questionInputs(d.questions),
      quiz_settings: d.quiz_settings,
      sources: d.sources,
      change_summary: d.change_summary && d.change_summary.trim() ? d.change_summary : null,
      estimated_minutes: d.estimated_minutes ?? 1,
    };
  }, []);

  const onSaved = useCallback((detail: VersionDetail, fresh: boolean) => {
    setMeta((m) => ({ ...m, version: detail.version, course: detail.course ?? m.course, lint: detail.lint, impact: detail.impact }));
    if (fresh && typeof detail.version?.estimated_minutes === 'number') {
      // A standard course's minutes follow the lint's estimate on the server (18.17).
      draftRef.current = { ...draftRef.current, estimated_minutes: detail.version.estimated_minutes };
      setDraft(draftRef.current);
    }
  }, []);

  // 8.4: the autosave is paused while a section run (or an edit run, editor plan 8.5) rewrites the draft.
  const autosave = useDraftAutosave({ versionId: version.id, enabled: editable, read, onSaved });
  const { schedule, flush, drop: dropUnsaved, hasUnsaved } = autosave;

  const update = useCallback(
    <K extends keyof DraftState>(field: K, value: DraftState[K]) => {
      if (!editable) return;
      draftRef.current = { ...draftRef.current, [field]: value };
      setDraft(draftRef.current);
      schedule([field as DraftField]);
    },
    [editable, schedule]
  );

  /**
   * Editor plan 8.5: read the version again after an AI write; a changed
   * content hash replaces the editor in place (one read at a time, so the
   * run's end and the panel's onApplied never reload twice). An edit made in
   * the last seconds and not saved yet would overwrite the AI's write, so it
   * is dropped and the admin is told (G10). A failed read falls back to the
   * full reload, which shows the failure and a Retry.
   */
  const aiRefresh = useRef<Promise<'changed' | 'same'> | null>(null);
  const refreshAfterAi = useCallback(
    (words: string): Promise<'changed' | 'same'> => {
      if (aiRefresh.current) return aiRefresh.current;
      const task = (async (): Promise<'changed' | 'same'> => {
        let fresh: VersionDetail;
        try {
          fresh = await getVersion(version.id);
        } catch {
          onReload();
          return 'changed';
        }
        const known = metaRef.current.version.content_sha256 ?? null;
        if ((fresh.version?.content_sha256 ?? null) === known) return 'same';
        if (hasUnsaved()) {
          dropUnsaved();
          toast('Edits you made in the last few seconds were not saved: the draft changed under them. Make them again if you still want them.', { type: 'error' });
        }
        toast(words);
        onReplace(fresh);
        return 'changed';
      })();
      aiRefresh.current = task;
      void task.finally(() => {
        aiRefresh.current = null;
      });
      return task;
    },
    [version.id, onReload, onReplace, toast, hasUnsaved, dropUnsaved]
  );

  // 8.4: when the section run ends: done reloads the version; otherwise the cause is shown and editing comes back.
  // Editor plan 8.5: when an edit run ends, an applied message reloads the draft in place; every other outcome unlocks without a reload.
  const { ended: endRun } = vb;
  useEffect(() => {
    const run = poll.detail?.run;
    if (!run || !vb.active || run.id !== vb.active.id || !isTerminalRunStatus(run.status)) return;
    if (handledRun.current === run.id) return;
    handledRun.current = run.id;
    if (isEditRun(run)) {
      const message = editsRef.current.find((m) => m.run_id === run.id) ?? null;
      const words = `Edit with AI applied ${message ? `message ${message.ordinal}` : 'the message'} and the draft was reloaded with it. Read the change, then accept or revert it.`;
      void (async () => {
        const outcome = run.status === 'done' ? await refreshAfterAi(words) : 'same';
        if (outcome === 'same') {
          endRun();
          refreshEdits();
        }
      })();
      return;
    }
    if (run.status === 'done') {
      toast('The builder finished rewriting the section. Read it before publishing.');
      onReload();
      return;
    }
    setRunOutcome(
      run.status === 'cancelled'
        ? 'The section run was cancelled; the draft is as it was, and you can edit it again.'
        : `The builder could not rewrite the section: ${explainError(run.error_code, run.error, 'the run failed.')} The draft is as it was, and you can edit it again.`
    );
    endRun();
  }, [poll.detail, vb.active, toast, onReload, endRun, refreshAfterAi, refreshEdits]);

  // Editor plan 8.5: a message's run is found by reading the version's run again (soon after a Send, then every 10 seconds).
  const { adopt } = vb;
  const lastSendAt = useRef<number | null>(null);
  const [sendTick, setSendTick] = useState(0);
  const watchRun = editorOn && aiAllowed && isDraft && vb.loaded && vb.active === null;
  useEffect(() => {
    if (!watchRun || !visible) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const sentAt = lastSendAt.current;
    const delays = sentAt !== null && Date.now() - sentAt < SEND_WATCH_WINDOW_MS ? SEND_WATCH_DELAYS_MS : [];
    let step = 0;
    const nextDelay = () => (step < delays.length ? delays[step++] : EDITOR_RUN_POLL_MS);
    const readRun = async () => {
      const run = await getVersionRun(version.id).catch(() => null);
      if (cancelled) return;
      if (run && run.version_id === version.id && isBuildingStatus(run.status)) {
        // A failed message retried from its card runs again under the same run id (18.3): its end is handled afresh.
        if (handledRun.current === run.id) handledRun.current = null;
        adopt(run);
        return;
      }
      timer = setTimeout(() => void readRun(), nextDelay());
    };
    timer = setTimeout(() => void readRun(), nextDelay());
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [watchRun, visible, sendTick, version.id, adopt]);

  // The message list follows the lock: a run that starts is named by its message (18.5).
  const activeRunId = vb.active ? vb.active.id : null;
  useEffect(() => {
    if (activeRunId) refreshEdits();
  }, [activeRunId, refreshEdits]);

  // 8.2: Send (and the card's Try again and Revert) first flushes the autosave; a failed save stops it (the SectionActions rule).
  const onBeforeSend = useCallback(async (): Promise<boolean> => {
    if (!editableRef.current) return false;
    const saved = await flush();
    if (!saved) {
      toast('Your latest edits could not be saved, so Edit with AI did not go ahead. Save the draft, then try again.', { type: 'error' });
      return false;
    }
    lastSendAt.current = Date.now();
    setSendTick((t) => t + 1);
    return true;
  }, [flush, toast]);

  // 8.5: the panel reports an applied message (or a revert): read the draft again and reload it in place when it changed.
  const onPanelApplied = useCallback(() => {
    refreshEdits();
    void refreshAfterAi('The draft was reloaded with the latest change from Edit with AI.');
  }, [refreshEdits, refreshAfterAi]);

  // 8.1: ?edit=1 is the panel's open state.
  const editParam = searchParams.get('edit') === '1';
  const setPanelOpen = useCallback(
    (open: boolean) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (open) next.set('edit', '1');
          else next.delete('edit');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const historyCount = edits.messages.length;
  const panelAvailable = editorOn && aiAllowed && (isDraft || historyCount > 0);
  const panelOpen = panelAvailable && editParam;

  // 8.7: the Sources panel's "Show message n" opens the panel and brings the message into view.
  const showMessage = useCallback(
    (messageId: string) => {
      setPanelOpen(true);
      window.setTimeout(() => {
        const byId = MESSAGE_ID_PATTERN.test(messageId) ? document.querySelector(`[data-message-id="${messageId}"]`) : null;
        const target = byId || document.getElementById('lms-edit-panel-column');
        if (target && typeof (target as HTMLElement).scrollIntoView === 'function') {
          (target as HTMLElement).scrollIntoView({ block: 'nearest' });
        }
      }, 0);
    },
    [setPanelOpen]
  );

  const panelSections = useMemo(
    () => (Array.isArray(draft.content.sections) ? draft.content.sections : []).map((s) => ({ id: s.id, title: s.title })),
    [draft.content.sections]
  );
  const editNotes = useMemo(() => editNotesOf(edits.messages), [edits.messages]);
  const notesSummary = withEditNotes(draft.change_summary, editNotes);
  const applyEditNotes = () => {
    if (!editable || notesSummary === null) return;
    update('change_summary', notesSummary);
    toast('The edit notes were added to the change summary. Reword it in the summary field before publishing if you like.');
  };

  const sectionNumberOf = (sectionId: string | null | undefined): number | null => {
    if (!sectionId) return null;
    const i = (Array.isArray(draft.content.sections) ? draft.content.sections : []).findIndex((s) => s.id === sectionId);
    return i >= 0 ? i + 1 : null;
  };

  const errors = useMemo(() => (Array.isArray(meta.lint?.errors) ? meta.lint.errors : []), [meta.lint]);
  const blocking = blockingErrors(meta.lint);
  const questionErrors = (i: number) => errors.filter((e) => lintQuestionIndex(e.path) === i).length;

  const recheck = async () => {
    setBusy('lint');
    try {
      await flush();
      const lint = await lintVersion(version.id);
      setMeta((m) => ({ ...m, lint }));
    } catch (err) {
      toast(apiErrorMessage(err, 'The lint could not be run.'), { type: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const openPreview = async () => {
    setBusy('preview');
    const saved = await flush();
    setBusy(null);
    if (!saved && isDraft) {
      toast('Your latest edits are not saved yet; the preview shows the last saved draft.', { type: 'info' });
    }
    navigate(catalogPaths.preview(version.id));
  };

  const discard = async () => {
    setBusy('discard');
    try {
      autosave.drop();
      await discardDraft(version.id, 'draft discarded in the catalog editor');
      toast(`Draft version ${version.version_no} discarded.`);
      navigate(catalogPaths.course(course.id));
    } catch (err) {
      toast(apiErrorMessage(err, 'The draft could not be discarded.'), { type: 'error' });
      setBusy(null);
      setDiscardOpen(false);
    }
  };

  const onPublished = (result: PublishResult) => {
    setPublishOpen(false);
    const sent = result.campaign ? `, ${result.campaign.recipient_count ?? 0} assignment(s) sent` : '';
    const superseded = result.superseded_completions > 0 ? `, ${result.superseded_completions} completion(s) superseded` : '';
    toast(`Version ${result.version?.version_no ?? version.version_no} of ${course.title} published${superseded}${sent}.`);
    navigate(catalogPaths.course(course.id));
  };

  const onLintRefused = (lint: LintResult) => setMeta((m) => ({ ...m, lint }));

  const tabs = [
    { value: 'content', label: 'Content' },
    ...(hasQuiz ? [{ value: 'questions', label: `Questions (${draft.questions.length})` }] : []),
    { value: 'settings', label: hasQuiz ? 'Settings and sources' : 'Sources and summary' },
  ];

  // 18.2 Q16: a builder-written clinical_skills draft needs a current clinician sign-off (fail closed while unknown).
  // Editor plan 18.2 Q3: so does a clinical draft Edit with AI changed (the server answers required for it).
  const signoffReason = (() => {
    if (!isDraft || !clinical) return null;
    if (!builder.checked) return 'Checking the clinician sign-off...';
    if (!builder.available) return null;
    if (!vb.loaded) return 'Checking the clinician sign-off...';
    if (vb.signoffError) return 'The clinician sign-off could not be checked.';
    if (vb.signoff && vb.signoff.required && !vb.signoff.current) {
      return vb.origin
        ? 'A clinical skills course the builder wrote needs a current clinician sign-off before it is published.'
        : 'A clinical skills course changed with Edit with AI needs a current clinician sign-off before it is published.';
    }
    return null;
  })();

  // Editor plan 18.2 Q4, 8.6: Publish is held while an applied message waits for Accept or Revert (fail closed while unknown).
  const editsReason = (() => {
    if (!isDraft || !editorOn || !aiAllowed) return null;
    if (!edits.loaded) return 'Checking the AI edits...';
    if (edits.error) return 'The AI edits could not be checked.';
    if (edits.pending > 0) return `Accept or revert the AI edits first (${edits.pending} waiting).`;
    return null;
  })();

  const publishBlockedReason = !isDraft
    ? null
    : locked
      ? editLocked
        ? `Edit with AI is working on ${activeMessage ? `message ${activeMessage.ordinal}` : 'a message'}; publish when it finishes.`
        : 'The builder is rewriting a section; publish when it finishes.'
      : editsReason
        ? editsReason
        : blocking.length > 0
          ? 'Fix the lint problems above to publish.'
          : autosave.state === 'error'
            ? 'The draft is not saved.'
            : signoffReason;

  const showSources =
    builder.available && isDraft && vb.loaded && (vb.origin !== null || vb.hasDoneRun || vb.hasDoneEditRun) && tab !== 'settings';
  const sectionActions =
    builder.available && isDraft && vb.loaded
      ? (section: { id: string }, index: number) => (
          <SectionActions
            versionId={version.id}
            sectionId={section.id}
            sectionNumber={index + 1}
            passesUsed={vb.passesUsed}
            passesMax={vb.passesMax}
            needsFile={!vb.hasDoneRun}
            disabled={locked}
            beforeRun={flush}
            onStarted={(run) => {
              setRunOutcome(null);
              vb.started(run);
              toast(`The builder is rewriting section ${index + 1}. The draft is read-only until it finishes.`, { type: 'info' });
            }}
          />
        )
      : undefined;

  return (
    <div className="space-y-4" data-testid="lms-catalog-editor" data-version-status={version.status}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <Link to={catalogPaths.course(course.id)} className={`inline-flex items-center gap-1 text-sm ${linkCls}`}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {course.title}
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{`Version ${version.version_no}`}</h2>
            <span className={pillCls(STATUS_TONES[version.status])} data-testid="lms-version-status">
              {STATUS_LABELS[version.status]}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{KIND_LABELS[course.kind]}</span>
          </div>
          <p className={`${hintCls} max-w-3xl`}>{profileHint(course.category)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isDraft ? <SaveStatus state={autosave.state} error={autosave.error} savedAt={autosave.savedAt} onRetry={() => void flush()} /> : null}
          <button type="button" className={outlineBtnCls()} onClick={openPreview} disabled={busy === 'preview'} data-testid="lms-open-preview">
            <Eye className="h-4 w-4" aria-hidden="true" />
            Preview
          </button>
          {course.current_version_id && course.current_version_id !== version.id ? (
            <Link to={catalogPaths.diff(version.id, course.current_version_id)} className={outlineBtnCls()} data-testid="lms-open-diff">
              <GitCompare className="h-4 w-4" aria-hidden="true" />
              Compare with current
            </Link>
          ) : null}
          {isDraft && editorOn && aiAllowed ? (
            <button
              type="button"
              className={outlineBtnCls(panelOpen)}
              onClick={() => setPanelOpen(!panelOpen)}
              aria-expanded={panelOpen}
              aria-controls={panelOpen ? 'lms-edit-panel-column' : undefined}
              data-testid="lms-edit-with-ai"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              Edit with AI
            </button>
          ) : null}
          {!isDraft && panelAvailable ? (
            <button
              type="button"
              className={outlineBtnCls(panelOpen)}
              onClick={() => setPanelOpen(!panelOpen)}
              aria-expanded={panelOpen}
              aria-controls={panelOpen ? 'lms-edit-panel-column' : undefined}
              data-testid="lms-edit-history-link"
            >
              <History className="h-4 w-4" aria-hidden="true" />
              {`Edited with AI: ${historyCount} message${historyCount === 1 ? '' : 's'}`}
            </button>
          ) : null}
          {isDraft ? (
            <>
              <button type="button" className={outlineBtnCls()} onClick={() => setDiscardOpen(true)} disabled={locked} data-testid="lms-discard-draft">
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                Discard draft
              </button>
              <button
                type="button"
                className={primaryBtnCls}
                onClick={() => setPublishOpen(true)}
                disabled={publishBlockedReason !== null}
                title={publishBlockedReason ?? undefined}
                data-testid="lms-publish"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                Publish
              </button>
            </>
          ) : null}
        </div>
      </div>

      {!isDraft ? (
        <div className={noticeCls('primary')} role="status" data-testid="lms-read-only">
          {`Version ${version.version_no} is ${STATUS_LABELS[version.status].toLowerCase()}. A published version never changes (G7): to change the course, start a new draft from its page.`}
        </div>
      ) : null}

      {isDraft && vb.origin ? (
        <BuilderNotice
          origin={vb.origin}
          sources={vb.originSources}
          content={draft.content}
          diffTo={course.current_version_id && course.current_version_id !== version.id ? catalogPaths.diff(version.id, course.current_version_id) : null}
        />
      ) : null}
      {isDraft && builder.available && vb.loaded && clinical && (vb.origin || vb.hasEditRun) ? (
        <ClinicalSignoffBlock
          versionId={version.id}
          state={vb.signoff}
          error={vb.signoffError}
          aiOnly={!vb.origin}
          onRecorded={vb.refresh}
          onRetry={vb.refresh}
        />
      ) : null}
      {locked && liveRun ? (
        <div
          className={noticeCls('primary')}
          role="status"
          aria-live="polite"
          data-testid="lms-builder-rewriting"
          data-run-mode={isEditRun(liveRun) ? 'edit' : liveRun.mode}
        >
          <Loader2 className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin" aria-hidden="true" />
          <p>
            {isEditRun(liveRun)
              ? `${editLockLine(activeMessage)} `
              : liveRun.mode === 'section'
                ? `The builder is rewriting ${sectionNumberOf(liveRun.targets?.section_id) ? `section ${sectionNumberOf(liveRun.targets?.section_id)}` : 'a section'}; this draft is read-only until it finishes. `
                : 'The builder is writing this draft; it is read-only until it finishes. '}
            <Link to={builderPaths.run(liveRun.id)} className={linkCls}>
              Follow the run
            </Link>
          </p>
        </div>
      ) : null}
      {isDraft && runOutcome ? (
        <div className={noticeCls('red')} role="alert" data-testid="lms-builder-run-outcome">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <div className="space-y-1">
            <p>{runOutcome}</p>
            <button type="button" className={`${linkCls} text-sm`} onClick={() => setRunOutcome(null)}>
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {isDraft ? <LintPanel lint={meta.lint} onRecheck={recheck} checking={busy === 'lint'} /> : null}
      {isDraft && publishBlockedReason ? (
        <p className="text-xs text-red-700 dark:text-red-400" data-testid="lms-publish-blocked">
          {publishBlockedReason}
        </p>
      ) : null}

      <div
        className={panelOpen ? 'grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_26rem]' : undefined}
        data-testid="lms-editor-body"
        data-panel={panelOpen ? 'open' : 'closed'}
      >
        {panelOpen ? (
          <aside
            id="lms-edit-panel-column"
            className="min-w-0 space-y-3 xl:sticky xl:top-16 xl:order-2 xl:max-h-[calc(100vh-5rem)] xl:self-start xl:overflow-y-auto"
            aria-label="Edit with AI"
            data-testid="lms-edit-panel-column"
          >
            <EditPanel
              versionId={version.id}
              courseId={course.id}
              sections={panelSections}
              isDraft={isDraft}
              locked={isDraft ? locked : true}
              onApplied={onPanelApplied}
              onBeforeSend={onBeforeSend}
              selectedSectionId={isDraft ? selectedSectionId : null}
            />
            {isDraft && editNotes.length > 0 ? (
              <div className={`${cardCls} space-y-2`} data-testid="lms-edit-notes">
                <p className={hintCls}>
                  Each applied message carries a one-sentence change note. Add them to the change summary learners see under What changed, then
                  reword it if you like.
                </p>
                <button
                  type="button"
                  className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`}
                  onClick={applyEditNotes}
                  disabled={!editable || notesSummary === null}
                  data-testid="lms-use-edit-notes"
                >
                  <ListPlus className="h-4 w-4" aria-hidden="true" />
                  Use the edit notes
                </button>
                {notesSummary === null ? (
                  <p className={hintCls} data-testid="lms-edit-notes-used">
                    The change summary already holds every edit note.
                  </p>
                ) : null}
              </div>
            ) : null}
          </aside>
        ) : null}

        <div className="min-w-0 space-y-4 xl:order-1">
          {!isDraft ? <VersionPreview detail={meta} /> : null}

          {isDraft ? <SegmentedControl label="Editor sections" options={tabs} value={tab} onChange={(v) => setTab(v as EditorTab)} /> : null}

          {showSources ? (
            <SourcesPanel
              versionId={version.id}
              content={draft.content}
              questions={draft.questions}
              sectionId={selectedSectionId}
              onSectionChange={setSelectedSectionId}
              sources={vb.originSources}
              messages={editorOn ? edits.messages : null}
              onShowMessage={editorOn && aiAllowed ? showMessage : undefined}
            />
          ) : null}

          {isDraft ? (
          <div className="min-w-0">
            {tab === 'content' ? (
              <ContentEditor
                content={draft.content}
                onChange={(content) => update('content', content)}
                category={course.category}
                kind={course.kind}
                errors={errors}
                locked={locked}
                onSelect={setSelectedSectionId}
                sectionActions={sectionActions}
              />
            ) : null}
            {tab === 'questions' && hasQuiz ? (
              <fieldset className="min-w-0" disabled={locked}>
              <QuestionsEditor
                questions={draft.questions}
                onChange={(questions) => update('questions', questions)}
                sections={Array.isArray(draft.content.sections) ? draft.content.sections : []}
                objectives={Array.isArray(draft.content.learning_objectives) ? draft.content.learning_objectives : []}
                drawCount={typeof (draft.quiz_settings as Partial<QuizSettings>).draw_count === 'number' ? (draft.quiz_settings as QuizSettings).draw_count : null}
                scenario={profile === 'scenario'}
                errorCount={questionErrors}
              />
              </fieldset>
            ) : null}
            {tab === 'settings' ? (
              <fieldset className="min-w-0" disabled={locked}>
              <SettingsEditor
                hasQuiz={hasQuiz}
                quizSettings={draft.quiz_settings as Partial<QuizSettings>}
                onQuizSettings={(settings) => update('quiz_settings', settings as VersionQuizSettings)}
                estimatedMinutes={draft.estimated_minutes}
                onEstimatedMinutes={(minutes) => update('estimated_minutes', minutes)}
                minutesFollowEstimate={profile === 'standard'}
                changeSummary={draft.change_summary ?? ''}
                onChangeSummary={(summary) => update('change_summary', summary)}
                versionNo={version.version_no}
                sources={draft.sources}
                onSources={(sources) => update('sources', sources)}
                minimumSources={minimumSources(course.category)}
              />
              </fieldset>
            ) : null}
          </div>
          ) : null}
        </div>
      </div>

      {publishOpen ? (
        <PublishDialog
          isOpen
          onClose={() => setPublishOpen(false)}
          versionId={version.id}
          versionNo={version.version_no}
          courseTitle={course.title}
          impact={meta.impact}
          changeSummary={draft.change_summary}
          beforePublish={flush}
          onPublished={onPublished}
          onLintRefused={onLintRefused}
        />
      ) : null}

      <ConfirmModal
        isOpen={discardOpen}
        onClose={() => setDiscardOpen(false)}
        onConfirm={discard}
        title={`Discard draft version ${version.version_no}?`}
        message="The draft is set aside (retired) and the course keeps its current version. Nothing a learner holds changes."
        confirmLabel="Discard draft"
        danger
        loading={busy === 'discard'}
      />
    </div>
  );
}

/** The route element: loads the version, then the editor keyed by its id. */
export function VersionEditor() {
  const { versionId } = useParams();
  const [detail, setDetail] = useState<VersionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  // Builder plan 8.4: a finished section run reloads the version in place (a new generation remounts the editor).
  const [generation, setGeneration] = useState(0);
  const reload = useCallback(() => {
    if (!versionId) return;
    getVersion(versionId)
      .then((d) => {
        setDetail(d);
        setGeneration((g) => g + 1);
      })
      .catch((err) => setError(apiErrorMessage(err, 'The version could not be loaded.')));
  }, [versionId]);
  // Editor plan 8.5: an AI write already read again by the editor is shown in place the same way.
  const replace = useCallback((d: VersionDetail) => {
    setDetail(d);
    setGeneration((g) => g + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    if (!versionId) {
      setError('No version was named.');
      return undefined;
    }
    getVersion(versionId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'The version could not be loaded.'));
      });
    return () => {
      cancelled = true;
    };
  }, [versionId, tick]);

  if (error) {
    return (
      <div className="space-y-3" data-testid="lms-catalog-editor-error">
        <div className={noticeCls('red')} role="alert">
          {error}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={outlineBtnCls()} onClick={() => setTick((t) => t + 1)}>
            Retry
          </button>
          <Link to={catalogPaths.list()} className={outlineBtnCls()}>
            Back to the catalog
          </Link>
        </div>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400" role="status">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
        Loading the version...
      </div>
    );
  }
  return <LoadedEditor key={`${detail.version.id}:${generation}`} initial={detail} onReload={reload} onReplace={replace} />;
}

export default VersionEditor;
